import React, { useState } from 'react';
import { PageSection, SiteData } from '../../../types';
import { ChevronDown, HelpCircle, Sparkles } from 'lucide-react';
import { isDarkBackground, getCardStylesForBackground, getOptimalTextColor } from '../../../utils/contrast';

interface SectionProps {
  section: PageSection;
  siteData: SiteData;
}

export const FaqSection: React.FC<SectionProps> = ({ section, siteData }) => {
  const content = section.content || {};
  const style = section.style || {};

  const badge = content.badge || 'SIKÇA SORULAN SORULAR';
  const title = content.title || 'Aklınıza Takılan Soruların Yanıtları';
  const items = Array.isArray(content.items) ? content.items : [];

  const [openIdx, setOpenIdx] = useState<number | null>(0);

  const bgColor = style.backgroundColor || '#F8FAFC';
  const isDark = isDarkBackground(bgColor);
  const cardStyles = getCardStylesForBackground(bgColor);
  const textColor = style.textColor || getOptimalTextColor(bgColor);
  const accentColor = siteData.settings?.accentColor || '#2563EB';

  const anchorId = style.anchorId || style.customId || 'faq';

  return (
    <section
      id={anchorId}
      data-section-id={section.id}
      data-section-type="FAQ"
      className="py-20 px-4 sm:px-6 lg:px-8 select-none transition-colors"
      style={{ backgroundColor: bgColor, color: textColor }}
    >
      <div className="max-w-4xl mx-auto space-y-12">
        <div className="text-center max-w-2xl mx-auto space-y-3">
          {badge && (
            <div
              className="inline-flex items-center gap-1.5 px-3.5 py-1 rounded-full text-xs font-black tracking-widest uppercase border"
              style={{
                borderColor: `${accentColor}40`,
                backgroundColor: `${accentColor}10`,
                color: accentColor,
              }}
            >
              <Sparkles className="w-3 h-3" />
              <span>{badge}</span>
            </div>
          )}
          <h2 className={`text-2xl sm:text-4xl font-extrabold tracking-tight ${isDark ? 'text-white' : 'text-slate-900'}`}>
            {title}
          </h2>
        </div>

        {items.length === 0 ? (
          <div className="p-8 text-center rounded-2xl border border-dashed border-slate-200 dark:border-slate-800 max-w-sm mx-auto text-xs text-slate-400">
            Henüz sıkça sorulan soru eklenmedi.
          </div>
        ) : (
          <div className="space-y-4">
            {items.map((item: any, idx: number) => {
              const isOpen = openIdx === idx;
              return (
                <div
                  key={item.id || idx}
                  className={`rounded-2xl border overflow-hidden shadow-xs transition-all ${cardStyles.bg} ${cardStyles.border}`}
                >
                  <button
                    type="button"
                    onClick={() => setOpenIdx(isOpen ? null : idx)}
                    className={`w-full p-6 text-left font-bold text-sm sm:text-base flex items-center justify-between gap-4 transition ${cardStyles.heading}`}
                  >
                    <span>{item.title}</span>
                    <ChevronDown
                      className={`w-5 h-5 shrink-0 transition-transform ${
                        isOpen ? 'rotate-180 text-blue-500' : 'text-slate-400'
                      }`}
                    />
                  </button>
                  {isOpen && (
                    <div className={`px-6 pb-6 text-xs sm:text-sm leading-relaxed border-t border-slate-200/40 dark:border-slate-800 pt-4 animate-in fade-in ${cardStyles.mutedText}`}>
                      {item.description}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};
